/**
 * Tiny scorer for the command palette. Every whitespace-separated token in the query must appear
 * somewhere in the haystack (label first, then keywords); matches at the start of the label or of a word
 * rank higher. Returns 0 for "no match" and a positive score otherwise.
 */
export function scoreMatch(query: string, label: string, keywords: readonly string[] = []): number {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return 1;
  }
  const l = label.toLowerCase();
  const extra = keywords.join(' ').toLowerCase();
  let total = 0;
  for (const token of tokens) {
    const idx = l.indexOf(token);
    if (idx === 0) {
      total += 100;
    } else if (idx > 0 && /[\s\-/(]/.test(l[idx - 1] ?? '')) {
      total += 60;
    } else if (idx > 0) {
      total += 30;
    } else if (extra.includes(token)) {
      total += 20;
    } else {
      return 0;
    }
  }
  // Shorter labels win ties: "Patrons" before "Patron categories and attribute types".
  return total + Math.max(0, 20 - label.length / 4);
}

export function rankBy<T>(items: readonly T[], query: string, pick: (item: T) => { label: string; keywords?: readonly string[] }, limit = 50): T[] {
  return items
    .map((item) => {
      const { label, keywords } = pick(item);
      return { item, score: scoreMatch(query, label, keywords) };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}
